import axios from 'axios';
import { getImageUrl, ProfileApi, RegApi } from 'BackEnd/API';
import { NO_CHAT_ROUTES } from 'common/constants/routing.constants';
import { AccountService } from 'common/services/account.service';
import AuthStore from 'common/stores/auth.store';
import {
  trackAnalyticEvent,
  trackPageEvent,
} from 'common/utils/client-analytics.util';
import { arrayIntersection, compareBitMasks } from 'common/utils/common.utils';
import i18n from 'i18n';
import initFreshChat from 'initFreshChat';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  action,
  computed,
  observable,
  makeObservable,
  runInAction,
} from 'mobx';
import { log } from 'common/utils/logging.util';
import { Notifier } from '../services/notification.service';
import {
  checkProfileStage,
  INITIAL_USER_ACCESS_STATE,
  needInfoMapingForHaveRealAccount,
  prefilledFields,
  USER_ACCESS_CODES,
} from '../constants/profile.constants';
import { IS_ROCKWEST } from '../utils/style.util';

class Profile {
  access = INITIAL_USER_ACCESS_STATE;

  @observable firstName = '';

  @observable patronymicName = '';

  @observable lastName = '';

  @observable nickName = '';

  @observable userGUID = null;

  @observable isMale = true;

  @observable isSwapFree = false;

  @observable email = '';

  @observable phone = null;

  @observable region = '';

  @observable metaData = {};

  @observable dateInfo = null;

  @observable contacts = {};

  @observable lastAddedPhone = null;

  @observable lastEddedEmails = [];

  @observable verificationMap = {};

  @observable isInitedFreshChat = false;

  @observable imageAvatar = '';

  @observable pspList = {};

  @observable awaitPhoneCode = false;

  @observable profileMetaData = {};

  @observable registrationPopupWasShown = false;

  @observable isFetched = false;

  @observable isDataFetched = false;

  constructor() {
    makeObservable(this);
  }

  @computed get getPspList() {
    return this.pspList;
  }

  @computed get needToShowMaintenanceWarning() {
    if (!this.isDataFetched) return false;
    return (
      !this.metaData.hasSeenMaintenanceWarning && +Date.now() < 1677974400000
    );
  }

  @computed get kycComplete() {
    return (
      this.kyc.hasFirstName &&
      this.kyc.hasLastName &&
      this.kyc.hasConfirmedEmail &&
      this.kyc.hasConfirmedPhone
    );
  }

  @computed get kyc() {
    const info = {
      hasFirstName: !!this.firstName,
      hasLastName: !!this.lastName,
    };

    const isConfirmed = (item) => item.is_confirmed && item.is_primary;

    info.hasConfirmedEmail = this.emails.some(isConfirmed);
    info.hasConfirmedPhone = this.phones.some(isConfirmed);
    return info;
  }

  @computed get needInfo() {
    const { emails, phones } = this;
    const email = emails.find((e) => e.is_primary);
    const phone = phones.find((p) => p.is_confirmed);

    if (!phone || !email) {
      return true;
    }

    return needInfoMapingForHaveRealAccount.some((key) => !this.metaData[key]);
  }

  @computed get needInfoAndHaveData() {
    return this.needInfo && this.isDataFetched;
  }

  @computed get providers() {
    return this.contacts?.providers || [];
  }

  @computed get registeredProviders() {
    return this.contacts?.registered || [];
  }

  @computed get avatarUrl() {
    return getImageUrl(this.imageAvatar);
  }

  @computed get getAwaitPhoneCode() {
    return this.awaitPhoneCode;
  }

  @computed get specialFieldsNotFilled() {
    const intersectedFields = arrayIntersection(
      checkProfileStage[this.stage],
      prefilledFields
    );
    return intersectedFields.some((field) => !this.state.options[field]);
  }

  @computed get isProfileCompleted() {
    return !!(
      this.emailIsConfirmed &&
      this.phoneIsConfirmed &&
      !this.specialFieldsNotFilled
    );
  }

  getProvider = (name) =>
    this.providers.find((item) => item.provider_name === name) || {};

  getContactsByName = (name) => {
    const findByName = (item) => item.provider_name === name;

    return this.registeredProviders.filter(findByName).map((item) => ({
      ...item,
      provider: this.getProvider(name),
    }));
  };

  @computed get phoneIsConfirmed() {
    return this.phones.some((phone) => phone.is_confirmed);
  }

  @computed get emailIsConfirmed() {
    return this.emails.some((email) => email.is_confirmed);
  }

  @computed get facebookProvider() {
    return (
      this.getContactsByName('fb')[0] || {
        provider: this.getProvider('fb'),
        is_confirmed: false,
      }
    );
  }

  @computed get emailProvider() {
    return this.getProvider('email');
  }

  @computed get phoneProvider() {
    return this.getProvider('phone');
  }

  @computed get emails() {
    return this.getContactsByName('email');
  }

  @computed get phones() {
    return this.getContactsByName('phone');
  }

  @action setAwaitPhoneCode = (state) => {
    this.awaitPhoneCode = state;
  };

  @action setRegistrationPopupWasShown = (state) => {
    this.registrationPopupWasShown = state;
  };

  @action getProfileData = async () => {
    if (this.isDataFetched) {
      return this.needInfo;
    }
    try {
      await this.fetchProfileData();
      return this.needInfo;
    } catch (error) {
      log(error);
      return true;
    }
  };

  @action fetchProfileData() {
    return AccountService.getProfile({
      page: window.location.origin + window.location.pathname,
    })
      .then(
        action('fetchProfileData', (data) => {
          this.firstName = data.first_name;
          this.lastName = data.last_name;
          this.region = data.region;
          this.contacts = data.contacts;
          this.metaData = data.meta_data || {};
          this.pspList = data.psp_list || {};
          this.userGUID = data.guid;
          this.imageAvatar = data.meta_data?.image_avatar;
          this.nickName = data?.nick_name;
          this.access = Object.keys(USER_ACCESS_CODES).reduce(
            (acum, code) => ({
              ...acum,
              [code]: compareBitMasks(data.access, USER_ACCESS_CODES[code]),
            }),
            {}
          );

          const restoreId = data.meta_data?.restoreId;
          const constacts = data?.contacts?.registered;
          const email =
            constacts?.find(
              (contact) =>
                contact.provider_name === 'email' && contact.is_confirmed
            ) ||
            constacts?.find((contact) => contact.provider_name === 'email');
          const phone =
            constacts?.find(
              (contact) =>
                contact.provider_name === 'phone' && contact.is_confirmed
            ) ||
            constacts?.find((contact) => contact.provider_name === 'phone');

          const paramsFreshChart = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: email?.user_key || '',
            phone: phone?.user_key || '',
          };

          if (restoreId) {
            paramsFreshChart.restoreId = restoreId;
          }

          if (
            !this.isInitedFreshChat &&
            !NO_CHAT_ROUTES.includes(window.location.pathname)
          ) {
            initFreshChat({
              externalId: AuthStore.session.user_guid,
              ...paramsFreshChart,
              updateRestoreId: (id) => {
                this.updateRestoreId(id);
              },
              onInit: () => {
                runInAction(() => {
                  this.isInitedFreshChat = true;
                });
              },
              onChatOpen: () => {
                trackPageEvent('open_chat');
              },
            });
          }

          if (window.fcWidget) {
            window.fcWidget.user.setFirstName(data.first_name);

            window.fcWidget.user.setLastName(data.last_name);
          }
          runInAction(() => {
            this.isDataFetched = true;
          });

          return data;
        })
      )
      .catch((error) => {
        log(error);
      });
  }

  @action updateImage = ({ image, showNotification = true }) => {
    if (IS_ROCKWEST) trackPageEvent('save_photo');
    this.isFetched = true;
    ProfileApi.uploadImage(image)
      .then((data) => {
        this.updateProfile({
          image_avatar: data.guid,
          isNeedToast: showNotification,
        })
          .catch(() => {
            if (showNotification) {
              Notifier.error(i18n.t('common.notification.uploadImage.error'));
            }
          })
          .then(() => {
            if (showNotification) {
              Notifier.success(
                i18n.t('common.notification.uploadImage.success')
              );
            }
          })
          .finally(() => {
            this.isFetched = false;
          });
      })
      .finally(() => {
        this.isFetched = false;
      });
  };

  updateRestoreId(restoreId) {
    return this.updateProfile({ restoreId, isNeedToast: false });
  }

  @computed get formatedPhone() {
    const phoneNumber = parsePhoneNumberFromString(this.phone);
    if (phoneNumber) {
      return phoneNumber.formatInternational();
    }
    return '';
  }

  @action sendEmailVerificationLink(authToken, email, appGuid) {
    return RegApi.sendEmailVerify(authToken, email, appGuid);
  }

  @action getCode(phoneObject) {
    return axios
      .post(this.phoneProvider.register_url, {
        phone: phoneObject.user_key,
      })
      .then(({ data }) => {
        trackAnalyticEvent('menu-profile-next_mobilevercode');
        this.verificationMap[phoneObject.user_key] = data;
        return data;
      })
      .catch(() => {
        Notifier.error(i18n.t('common.notification.getVerificationCode.error'));
      });
  }

  @action sendCode(url, code) {
    return axios
      .get(url, {
        params: { code },
      })
      .then(() => {
        if (IS_ROCKWEST)
          trackAnalyticEvent('menu-profile-adjust_mobilevercode');
        Notifier.success(i18n.t('common.notification.verify.success'));
        return this.fetchProfileData();
      })
      .then(() =>
        AccountService.makePhonePrimary(
          this.phones.find((el) => el.is_confirmed).id
        )
      )
      .then(() =>
        Promise.allSettled(
          this.phones
            .filter((el) => !el.is_confirmed)
            .map((phone) => AccountService.deletePhone(phone.id))
        )
      )
      .catch((e) => {
        Notifier.error(i18n.t('common.notification.verify.error'));
        throw e;
      });
  }

  @action deletePhone(phoneObject = {}) {
    AccountService.deletePhone(phoneObject.id)
      .then(() => {
        this.fetchProfileData();
      })
      .then(() => {
        Notifier.success(i18n.t('common.notification.deletePhone.success'));
      })
      .catch(() => {
        Notifier.error(i18n.t('common.notification.deletePhone.error'));
      });
  }

  @action deleteSocial(socialObject = {}) {
    AccountService.deleteSocial(socialObject.id)
      .then(() => {
        this.fetchProfileData();
      })
      .then(() => {
        Notifier.success(i18n.t('common.notification.deleteSocial.success'));
      })
      .catch(() => {
        Notifier.error(i18n.t('common.notification.deleteSocial.error'));
      });
  }

  @action addEmail(email) {
    return axios
      .post(this.emailProvider.register_url, {
        email,
      })
      .then(() => {
        this.fetchProfileData();
        this.lastEddedEmails.push(email);
      })
      .catch(() => {
        Notifier.error(i18n.t('common.notification.addEmail.error'));
      });
  }

  @action sendEmailVerification(emailObject) {
    return axios
      .post(this.emailProvider.register_url, {
        email: emailObject.user_key,
      })
      .then(() => {
        this.fetchProfileData();
        Notifier.success(
          i18n.t('common.notification.sendEmailVerification.success')
        );
      })
      .catch(() => {
        Notifier.error(
          i18n.t('common.notification.sendEmailVerification.error')
        );
      });
  }

  @action deleteEmail(socialEmail = {}) {
    AccountService.deleteEmail(socialEmail.id)
      .then(() => {
        this.fetchProfileData();
      })
      .then(() => {
        Notifier.success(i18n.t('common.notification.deleteEmail.success'));
      })
      .catch(() => {
        Notifier.error(i18n.t('common.notification.deleteEmail.error'));
      });
  }

  @action updatePersonalInfo = async ({
    firstName,
    lastName,
    code,
    email,
    phone,
    region,
    ...data
  }) => {
    if (!this.isDataFetched) await this.fetchProfileData();

    if (typeof firstName !== 'undefined') {
      this.firstName = firstName;
    }

    if (typeof lastName !== 'undefined') {
      this.lastName = lastName;
    }

    if (email) {
      this.email = email;
    }

    if (!region && !this.region && this.metaData.countryOfResidence) {
      this.region = this.metaData.countryOfResidence;
    }

    if (region) {
      this.region = region;
    }

    this.profileMetaData = { ...data };

    return this.updateProfile({ isNeedToast: false });
  };

  updateProfile({
    restoreId,
    observablePrices,
    isNeedToast = true,
    image_avatar,
  } = {}) {
    const profile = {
      first_name: this.firstName,
      patronymic_name: this.patronymicName,
      last_name: this.lastName,
      nick_name: this.nickName,
      is_male: this.isMale,
      is_swap_free: this.isSwapFree,
      meta_data: this.metaData,
      date_info: this.dateInfo,
      region: this.region,
    };

    if (restoreId) {
      profile.meta_data = {
        ...profile.meta_data,
        restoreId,
      };
    }
    if (observablePrices) {
      profile.meta_data = {
        ...profile.meta_data,
        observablePrices,
      };
    }

    profile.meta_data = {
      ...profile.meta_data,
      ...this.profileMetaData,
    };

    if (image_avatar) {
      if (!profile.meta_data) profile.meta_data = {};
      profile.meta_data.image_avatar = image_avatar;
    }
    return AccountService.updateProfile({ ...profile, image_avatar })
      .then(() => {
        if (isNeedToast)
          Notifier.success(i18n.t('common.notification.updateProfile.success'));
        return this.fetchProfileData();
      })
      .catch(() => {
        if (isNeedToast)
          Notifier.error(i18n.t('common.notification.updateProfile.error'));
      });
  }
}

const ProfileStore = new Profile();

export default ProfileStore;
